import {
  NumberField as BaseUiNumberField,
  NumberFieldRoot
} from "@base-ui/react/number-field"
import React, { useMemo, useRef } from "react"
import { get, useController, useFormState } from "react-hook-form"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { Logger } from "shared/lib/Logger"
import { ENTER_KEY } from "shared/lib/keys"
import { getFormattingLocale } from "shared/lib/locale/formattingLocale"
import { useBaseUiClassName } from "shared/lib/useBaseUiClassName"
import { useEvent } from "shared/lib/useEvent"
import { useMergedRefs } from "shared/lib/useMergedRefs"
import { IconSize } from "shared/types/Icon"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import "assets/stylesheets/ui-kit/inputs/_input.scss"

import {
  Input as DefaultInput,
  InputChangeBehavior,
  InputContext,
  InputContextProvider,
  InputContextUserProps
} from "../Input"
import { InputFocus } from "../InputFocus"
import { useInputDescriptionIDs } from "../accessibility/useInputAriaDescription"
import { useInputContextProps } from "../hooks/useInputContextProps"
import { NumberInputErrorMessage } from "./components/NumberInputErrorMessage"
import { ReadonlyNumber } from "./components/ReadonlyNumber"
import "./styles/_number-input.scss"

export interface NumberInputContextType {
  min?: number
  max?: number
  format?: Intl.NumberFormatOptions
}

export const NumberInputContext = React.createContext<NumberInputContextType>(
  {}
)

interface NumberInputRootProps
  extends BaseUiNumberField.Root.Props,
    InputContextUserProps,
    NumberInputContextType {}

const RootBase = React.forwardRef<
  HTMLDivElement,
  NumberInputRootProps & { changeBehavior?: InputChangeBehavior }
>((props, ref) => {
  const { min, max, format } = props
  const {
    className,
    changeBehavior = InputChangeBehavior.CHANGE,
    onValueChange,
    defaultValue,
    value,
    onKeyDown,
    onBlur,
    ...remainingProps
  } = props
  const { inputContextProps, restProps, id } =
    useInputContextProps(remainingProps)

  const getClassName = useBaseUiClassName("input number-input", className)

  const numberInputContext = useMemo(
    () => ({ min, max, format }),
    [min, max, format]
  )

  const internalValueRef = useRef<
    [number | null, NumberFieldRoot.ChangeEventDetails | null]
  >([value ?? defaultValue ?? null, null])

  const handleValueChange: BaseUiNumberField.Root.Props["onValueChange"] =
    useEvent((nextValue, eventDetails) => {
      internalValueRef.current = [nextValue, eventDetails]
      if (changeBehavior === InputChangeBehavior.CHANGE) {
        onValueChange?.(nextValue, eventDetails)
      }
      if (
        changeBehavior === InputChangeBehavior.BLUR &&
        eventDetails.reason === "input-blur"
      ) {
        onValueChange?.(nextValue, eventDetails)
      }
    })

  const handleKeyDown: BaseUiNumberField.Root.Props["onKeyDown"] = useEvent(
    event => {
      if (
        event.key === ENTER_KEY.key &&
        changeBehavior === InputChangeBehavior.BLUR &&
        internalValueRef.current[1]
      ) {
        onValueChange?.(
          internalValueRef.current[0],
          internalValueRef.current[1]
        )
      }
      onKeyDown?.(event)
    }
  )

  const handleBlur: BaseUiNumberField.Root.Props["onBlur"] = useEvent(event => {
    if (
      changeBehavior === InputChangeBehavior.BLUR &&
      internalValueRef.current[1]
    ) {
      onValueChange?.(internalValueRef.current[0], internalValueRef.current[1])
    }
    onBlur?.(event)
  })

  return (
    <NumberInputContext.Provider value={numberInputContext}>
      <InputContextProvider {...inputContextProps}>
        <BaseUiNumberField.Root
          {...restProps}
          id={id}
          ref={ref}
          className={getClassName}
          locale={getFormattingLocale()}
          value={value}
          defaultValue={defaultValue}
          onValueChange={handleValueChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
        />
      </InputContextProvider>
    </NumberInputContext.Provider>
  )
})
RootBase.displayName = "NumberFieldRootBase"

const HookFormRoot = React.forwardRef<
  HTMLDivElement,
  NumberInputRootProps & { changeBehavior?: InputChangeBehavior }
>((props, ref) => {
  const {
    name = "",
    className,
    hasError,
    min,
    max,
    inputRef,
    ...remainingProps
  } = props

  const { errors } = useFormState({ name })
  const error = get(errors, name)

  if (!name) {
    Logger.error("HookFormInput is missing a name")
  }

  const {
    field: { onChange, value, ref: controllerInputRef, ...field }
  } = useController({
    name,
    disabled: props.disabled,
    rules: {
      required: props.required,
      min,
      max
    }
  })

  const mergedRef = useMergedRefs(inputRef, controllerInputRef)

  return (
    <RootBase
      {...remainingProps}
      {...field}
      name={name}
      ref={ref}
      inputRef={mergedRef}
      className={classNames({ error: !!error }, className)}
      hasError={error || hasError}
      min={min}
      max={max}
      value={value ?? null}
      onValueChange={onChange}
    />
  )
})
HookFormRoot.displayName = "NumberFieldHookFormRoot"

const Root = React.forwardRef<
  HTMLDivElement,
  NumberInputRootProps & { changeBehavior?: InputChangeBehavior }
>((props, ref) => {
  const { name } = props

  if (name) {
    return <HookFormRoot {...props} ref={ref} />
  }
  return <RootBase {...props} ref={ref} />
})
Root.displayName = "NumberFieldRoot"

const Group = React.forwardRef<HTMLDivElement, BaseUiNumberField.Group.Props>(
  (props, ref) => {
    const { className, ...restProps } = props
    const getClassName = useBaseUiClassName("number-input__group", className)
    return (
      <BaseUiNumberField.Group
        {...restProps}
        ref={ref}
        className={getClassName}
      />
    )
  }
)
Group.displayName = "NumberFieldGroup"

const Input = React.forwardRef<HTMLInputElement, BaseUiNumberField.Input.Props>(
  (props, ref) => {
    const { t } = useTranslation()
    const { className, disabled: disabledProp, ...restProps } = props
    const { disabled: contextDisabled, label } = React.useContext(InputContext)
    const { ariaDescribedBy } = useInputDescriptionIDs({
      additionalDescriptions: restProps["aria-describedby"]
    })

    const disabled = disabledProp ?? contextDisabled

    const getClassName = useBaseUiClassName(
      "input__control number-input__input",
      className
    )
    return (
      <BaseUiNumberField.Input
        {...restProps}
        ref={ref}
        className={getClassName}
        disabled={disabled}
        aria-describedby={ariaDescribedBy}
        aria-roledescription={t("NumberField.RoleDescription")}
        // Input.Label points htmlFor at the Root's id, but base-ui generates a
        // separate id for the inner input, so the <label for> never associates.
        // Mirror the context label onto aria-label instead.
        aria-label={restProps["aria-label"] || label || undefined}
      />
    )
  }
)
Input.displayName = "NumberFieldInput"

const Increment = React.forwardRef<
  HTMLButtonElement,
  BaseUiNumberField.Increment.Props
>((props, ref) => {
  const { t } = useTranslation()
  const { className, children, disabled: disabledProp, ...restProps } = props
  const {
    disabled: contextDisabled,
    loading,
    readonly
  } = React.useContext(InputContext)

  const disabled = disabledProp ?? contextDisabled

  const getClassName = useBaseUiClassName(
    "btn btn-secondary number-input__increment",
    className
  )

  if (loading || readonly) {
    return null
  }

  return (
    <BaseUiNumberField.Increment
      {...restProps}
      ref={ref}
      className={getClassName}
      disabled={disabled}
      aria-label={t("NumberField.Increment")}
    >
      {children ?? <Icon name={IconName.PLUS} size={IconSize.XS} />}
    </BaseUiNumberField.Increment>
  )
})
Increment.displayName = "NumberFieldIncrement"

const Decrement = React.forwardRef<
  HTMLButtonElement,
  BaseUiNumberField.Decrement.Props
>((props, ref) => {
  const { t } = useTranslation()
  const { className, children, disabled: disabledProp, ...restProps } = props
  const {
    disabled: contextDisabled,
    loading,
    readonly
  } = React.useContext(InputContext)

  const disabled = disabledProp ?? contextDisabled

  const getClassName = useBaseUiClassName(
    "btn btn-secondary number-input__decrement",
    className
  )

  if (loading || readonly) {
    return null
  }

  return (
    <BaseUiNumberField.Decrement
      {...restProps}
      ref={ref}
      className={getClassName}
      disabled={disabled}
      aria-label={t("NumberField.Decrement")}
    >
      {children ?? <Icon name={IconName.MINUS} size={IconSize.XS} />}
    </BaseUiNumberField.Decrement>
  )
})
Decrement.displayName = "NumberFieldDecrement"

const ScrubArea = React.forwardRef<
  HTMLSpanElement,
  BaseUiNumberField.ScrubArea.Props
>((props, ref) => {
  const { className, ...restProps } = props
  const getClassName = useBaseUiClassName("number-input__scrub-area", className)
  return (
    <BaseUiNumberField.ScrubArea
      {...restProps}
      ref={ref}
      className={getClassName}
    />
  )
})
ScrubArea.displayName = "NumberFieldScrubArea"

const ScrubAreaCursor = React.forwardRef<
  HTMLSpanElement,
  BaseUiNumberField.ScrubAreaCursor.Props
>((props, ref) => {
  const { className, ...restProps } = props
  const getClassName = useBaseUiClassName(
    "number-input__scrub-area-cursor",
    className
  )
  return (
    <BaseUiNumberField.ScrubAreaCursor
      {...restProps}
      ref={ref}
      className={getClassName}
    >
      <CursorGrowIcon />
    </BaseUiNumberField.ScrubAreaCursor>
  )
})
ScrubAreaCursor.displayName = "NumberFieldScrubAreaCursor"

function CursorGrowIcon(props: React.ComponentProps<"svg">) {
  return (
    <svg
      width="26"
      height="14"
      viewBox="0 0 24 14"
      fill="black"
      stroke="white"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path d="M19.5 5.5L6.49737 5.51844V2L1 6.9999L6.5 12L6.49737 8.5L19.5 8.5V12L25 6.9999L19.5 2V5.5Z" />
    </svg>
  )
}

export const NumberInput = {
  ...DefaultInput,
  Root,
  Group,
  Input,
  Increment,
  Decrement,
  ScrubArea,
  ScrubAreaCursor,
  Focus: InputFocus,
  Readonly: ReadonlyNumber,
  ErrorMessage: NumberInputErrorMessage
}
